import { observer } from 'mobx-react';
import { ReactNode, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

// Components
import { LeftOutlined } from '@ant-design/icons';
import { Breadcrumb, Button } from 'top-component-library';

// Constants
import breakpoints from 'constants/breakpoints';

// Utils
import { useItemSheet } from 'hooks/use-item-sheet';
import { getHorizontalScrollPercentage, scrollElementHorizontally } from 'utils/Scroll';
import { buildBreadcrumbs } from './utils';

function ModifierBreadcrumb() {
  const { displayedItem, setDisplayedItem } = useItemSheet();
  const containerRef = useRef<HTMLDivElement>(null); // Breadcrumb won't accept a `ref`, so just place it on the Container.
  const [isScrollable, setIsScrollable] = useState<boolean>(false);
  const [scrollPercentage, setScrollPercentage] = useState<number>(0);

  // Controls fade effect when scrolling breadcrumb left/right.
  const handleScroll = (element: HTMLDivElement) => {
    const newScrollPercentage = getHorizontalScrollPercentage(element);
    setScrollPercentage(newScrollPercentage);
  };

  const items = buildBreadcrumbs(displayedItem);

  // Force breadcrumb to always show the last item in the list when a new modifier is selected.
  // Scroll position will be incorrect without the use of `setTimeout()`.
  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    setTimeout(() => {
      const breadcrumb = containerRef.current?.querySelector('.ant-breadcrumb');
      if (!breadcrumb) {
        return;
      }
      breadcrumb.scrollLeft = breadcrumb.scrollWidth;
      setIsScrollable(breadcrumb.scrollWidth > breadcrumb.clientWidth);
    }, 0);
  }, [displayedItem]);

  // Allow horizontal scrolling via the mouse wheel.
  useEffect(() => {
    if (!containerRef.current) {
      return undefined;
    }

    const breadcrumb = containerRef.current.querySelector('.ant-breadcrumb');
    const handleWheelScroll = (e: Event) => scrollElementHorizontally(e as WheelEvent, breadcrumb);
    breadcrumb?.addEventListener('wheel', handleWheelScroll, {
      passive: false,
    });
    return () => breadcrumb?.removeEventListener('wheel', handleWheelScroll);
  }, [displayedItem]);

  if (!displayedItem.parent) {
    return null;
  }

  return (
    <Container ref={containerRef}>
      <BackButton
        aria-label={`Back to ${items[0].name ?? ''}`}
        icon={<LeftOutlined />}
        onClick={() => setDisplayedItem(items[0])}
      />
      {/* @ts-expect-error */}
      <BreadcrumbModifed
        onScroll={(e: Event) => e.target && handleScroll(e.target as HTMLDivElement)}
        // TODO: this comment is wrong?
        scrollable={isScrollable ? 1 : 0} // Attribute cannot accept a boolean.
        scrollpercentage={scrollPercentage} // Custom attribute must be lowercase.
      >
        {items.map((item, index) => (
          <Item key={item.menu_item_id} onClick={() => setDisplayedItem(item)}>
            <ItemName aria-label={`Back to ${item.name}`} tabIndex={index === items.length - 1 ? -1 : 0} type="link">
              {item.name}
            </ItemName>
          </Item>
        ))}
      </BreadcrumbModifed>
    </Container>
  );
}

export default observer(ModifierBreadcrumb);

const BackButton = styled(Button)`
  align-items: center;
  border: 0;
  display: flex;
  margin-right: 10px;
  padding: 0;
  width: fit-content;

  &:focus,
  &:hover {
    opacity: 0.8;
  }

  .anticon {
    align-items: center;
    color: var(--color-neutral__title-active);
    display: flex;
  }
`;

const BreadcrumbModifed = styled(Breadcrumb)<{ scrollpercentage: number; scrollable: number }>`
  -ms-overflow-style: none; // Hide scrollbar: IE and Edge
  align-items: center;
  display: flex;
  flex-wrap: nowrap;
  // Fade left side when scrolling right; fade right side when scrolling left. Prevent fade when not scrollable.
  mask-image: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 1) ${({ scrollpercentage }) => Math.min(scrollpercentage, 10)}%,
    rgba(0, 0, 0, 1) ${({ scrollable, scrollpercentage }) => (scrollable ? 90 + scrollpercentage / 10 : 100)}%,
    rgba(0, 0, 0, 0) 100%
  );
  overflow-x: scroll;
  scrollbar-width: none; // Hide scrollbar: Firefox
  width: 100%;

  &::-webkit-scrollbar {
    display: none; // Hide scrollbar: Chrome, Safari, and Opera
  }

  // Container for each breadcrumb item & separator
  & > span {
    flex-shrink: 0;
  }
`;

const Container = styled.div`
  align-items: center;
  border-top: 6px solid var(--color-neutral__line);
  display: flex;
  padding: 0.25rem 1.25rem;
  width: 100%;

  @media (min-width: ${breakpoints.md}px) {
    border-bottom: 1px solid var(--color-neutral__line);
    border-top: 1px solid var(--color-neutral__line);
    margin-left: 1.5rem;
    margin-top: 0.5rem;
    padding: 0.25rem 0;
    width: calc(100% - 1.5rem);
  }
`;

const Item = styled(Breadcrumb.Item)<{ children: ReactNode; key: string; onClick: () => void }>`
  .ant-breadcrumb > span:last-child & {
    button {
      pointer-events: none;

      &:focus,
      &:hover {
        span {
          color: var(--color-neutral__title-active);
        }
      }
    }
  }

  .ant-breadcrumb > span:not(:last-child) & {
    & button {
      color: var(--color-neutral__title-active);
      opacity: 0.8;

      &:focus,
      &:hover {
        opacity: 1;

        span {
          color: var(--color-neutral__title-active);
        }
      }
    }
  }
`;

const ItemName = styled(Button)`
  color: var(--color-neutral__title-active);
  font-size: 0.625rem;
  font-weight: 500;
  height: auto;
  letter-spacing: initial;
  line-height: 12px;
  padding: 0;
  text-transform: none;

  &:hover {
    span {
      text-decoration: none;
    }
  }
`;
